<template>
  <BaseModal :modalTitle="$t('W: Skift placering')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit">
      <UiComboboxVee
        name="currentLocationID"
        :label="$t('W: Lokation')"
        :placeholder="$t('W: Ingen placering')"
        :options="locationOptions"
        v-model="form.currentLocationID" />

      <template #buttons>
        <UiBaseButton
          :text="$t('action.save')"
          :isLoading="isLoading"
          :showSaveIcon="showSaveIcon"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { useToast } from '@/globals/typescript/utils/toast'
import { useAppStore } from '@/stores/appStore'
import { useLabLocationStore } from '@/stores/labLocationStore'
import { useModalStore } from '@/stores/modalStore'
import { useOrderStore } from '@/stores/orderStore'
import { mapActions, mapState } from 'pinia'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

export default {
  name: 'ChangeSampleLocationModal',

  emits: ['close'],

  props: {
    orderID: { type: String, required: true },
    sampleID: { type: String, required: true },
    currentLocationID: { type: String as () => string | null, default: null },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: {
        currentLocationID: this.currentLocationID ?? '',
      },
      isLoading: false,
      showSaveIcon: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useAppStore, ['defaultMessageSaveIconTime']),
    ...mapState(useLabLocationStore, ['labLocations', 'activeLabLocations']),

    locationOptions(): { value: string; text: string }[] {
      const options = this.activeLabLocations
        .map((labLocation) => ({ value: labLocation.id, text: labLocation.name }))
        .sort((a, b) => a.text.localeCompare(b.text))

      // If the sample's current location has since gone inactive, keep it selectable
      // so it still shows as the selected value.
      const currentIsInactive =
        this.currentLocationID &&
        !this.activeLabLocations.some((x) => x.id === this.currentLocationID)
      if (currentIsInactive) {
        const inactiveLocation = this.labLocations.find(
          (x) => x.id === this.currentLocationID
        )
        if (inactiveLocation) {
          options.push({ value: inactiveLocation.id, text: inactiveLocation.name })
        }
      }

      return [{ value: '', text: this.$t('W: Ingen placering') }, ...options]
    },
  },

  methods: {
    ...mapActions(useModalStore, ['closeTop']),
    ...mapActions(useOrderStore, ['updateSampleLocation']),

    async onSubmit(): Promise<void> {
      this.isLoading = true
      this.errorText = ''
      try {
        await this.updateSampleLocation(
          this.orderID,
          this.sampleID,
          this.form.currentLocationID || null
        )
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        useToast().success(this.$t('W: Placering opdateret'))
        this.closeTop()
      } catch {
        useToast().error(this.$t('W: Kunne ikke opdatere placering'))
        this.errorText = this.$t('error.common.standard')
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
